import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { fetchQueryWithToken, getSettings, SettingsState } from '@vxui/common';
import {
  addTranscoderProfileTypeShared,
  cleanIdFromTranscoderProfile,
  TranscoderAudioProfile,
  TranscoderTemplate,
  TranscoderVideoProfile,
} from '../../models/transcoder';
import { transfromTranscoderErrors } from './transcoder.helpers';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverTranscoderConfigurator ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return fetchQueryWithToken(adjustedArgs, api, extraOptions);
};

const prepareBodyToUpdate = (body: TranscoderTemplate) => {
  body.videoProfiles = body.videoProfiles.map(
    profile => cleanIdFromTranscoderProfile(profile) as TranscoderVideoProfile,
  );
  body.audioProfiles = body.audioProfiles.map(
    profile => cleanIdFromTranscoderProfile(profile) as TranscoderAudioProfile,
  );
};

export const transcoderTemplateApi = createApi({
  reducerPath: 'transcoderTemplateApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Template'],
  endpoints: builder => ({
    getTranscoderTemplates: builder.query<TranscoderTemplate[], void>({
      query: () => 'template',
      transformResponse: (response: TranscoderTemplate[]) =>
        response.map(template => {
          const { audioProfiles, videoProfiles, ...restTemplate } = template;

          return {
            ...restTemplate,
            audioProfiles: audioProfiles.map(item => addTranscoderProfileTypeShared(item)),
            videoProfiles: videoProfiles.map(profile => {
              if (profile.width && profile.height) {
                profile.resolution = `${profile.width}x${profile.height}`;
              }

              return addTranscoderProfileTypeShared(profile);
            }),
          };
        }),
      providesTags: ['Template'],
    }),
    createTranscoderTemplate: builder.mutation<TranscoderTemplate, TranscoderTemplate>({
      query: template => {
        const body = { ...template };
        prepareBodyToUpdate(body);

        return {
          url: 'template',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Template'],
      transformResponse: (response: TranscoderTemplate) => {
        return response;
      },
      transformErrorResponse: (response: FetchBaseQueryError, meta, arg: TranscoderTemplate) =>
        transfromTranscoderErrors<TranscoderTemplate>(response, arg),
    }),
    updateTranscoderTemplate: builder.mutation<TranscoderTemplate, TranscoderTemplate>({
      query: template => {
        const body = { ...template };
        prepareBodyToUpdate(body);

        return {
          url: `template/${template.id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['Template'],
      transformErrorResponse: (response: FetchBaseQueryError, meta, arg: TranscoderTemplate) =>
        transfromTranscoderErrors<TranscoderTemplate>(response, arg),
    }),
    deleteTranscoderTemplate: builder.mutation<void, string>({
      query: uuid => ({
        url: `template/${uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Template'],
    }),
  }),
});

export const {
  useGetTranscoderTemplatesQuery,
  useCreateTranscoderTemplateMutation,
  useUpdateTranscoderTemplateMutation,
  useDeleteTranscoderTemplateMutation,
} = transcoderTemplateApi;
